import { useParams } from 'react-router-dom';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useApplicationFormSetup } from '../hooks/application-form-setup.hook';
import { FormContent } from './FormContent';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';
import { NotFound } from '@/features/help/components/NotFound';
import { TrialEnded } from './misc/TrialEnded';

// Purpose: Main component that handles the application form display and initialization
export function ApplicationForm() {
  const { userShortId, mappingShortId, signerShortId, recordId } = useParams<{
    userShortId: string;
    mappingShortId: string;
    signerShortId?: string;
    recordId?: string;
  }>();

  const { error, structure, isBrokerTrialEnded } = useApplicationStore();
  const { getVisiblePages } = useNavigationStore();

  // Use the custom hook to handle all setup logic
  const { isObjectTypeCheckLoading, isObjectTypeInvalid, imageLoading, loadingState, isReady } =
    useApplicationFormSetup({
      userShortId,
      mappingShortId,
      signerShortId,
      recordId,
    });

  // Show 404 if mapping is not for Lead object
  if (isObjectTypeInvalid) {
    logger.info('Application Form', 'Showing 404 due to invalid object type', {
      mappingShortId,
      hasStructure: !!structure,
      hasPages: !!structure?.pages?.length,
    });
    return <NotFound />;
  }

  // Show error state with support information (check error first before loading)
  if (error && !structure) {
    logger.error('Application Form', 'Showing error state due to explicit error', {
      error,
      hasStructure: !!structure,
    });
    return <NotFound />;
  }

  // Show trial ended message if broker's trial has ended (check before loading to avoid infinite spinner)
  if (isBrokerTrialEnded) {
    logger.warn('Application Form', 'Broker trial has ended', {
      userShortId,
      mappingShortId,
      recordId,
    });
    return <TrialEnded />;
  }

  // Show loading state while fetching data or loading images or checking object type
  if (loadingState || !structure || imageLoading || isObjectTypeCheckLoading) {
    logger.debug('Application Form', 'Showing loading spinner', {
      loadingState,
      imageLoading,
      isObjectTypeCheckLoading,
      hasStructure: !!structure,
      error: error || null,
    });
    return <FullScreenLoader message="Loading application..." />;
  }

  // Show error if structure is invalid
  const visiblePages = getVisiblePages();
  if (visiblePages.length === 0) {
    logger.error('Application Form', 'Showing error state due to invalid structure', {
      hasStructure: !!structure,
      pagesLength: visiblePages.length,
      structure: structure || null,
    });
    return <NotFound />;
  }

  // The form is ready to be displayed
  if (isReady) {
    logger.info('Application Form', 'Rendering form after successful setup', {
      pagesCount: visiblePages.length,
      firstPageFields: visiblePages[0]?.sections?.length || 0,
      hasLogo: !!structure.logo,
    });

    return (
      <div className="min-h-screen bg-gray-50 font-inter">
        <FormContent />
      </div>
    );
  }

  // Fallback loading state
  return <FullScreenLoader message="Loading application..." />;
}

export default ApplicationForm;
